import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Modal } from 'components/Modal';
import { findRichMessageTemplate } from 'constants/richMessageTemplates';
import { commonStrings, lineStrings } from 'constants/strings';
import {
  ERichMessageTemplateCategory,
  type TRichMessageTemplate,
} from 'types/richMessage';
import { RichMessageTemplateThumbnail } from './RichMessageTemplateThumbnail';

type Props = {
  open: boolean;
  selectedId: string;
  templates: TRichMessageTemplate[];
  onClose: () => void;
  onApply: (templateId: string) => void;
};

const strings = lineStrings.management.richMessage;

const CATEGORY_TABS: Array<{
  id: ERichMessageTemplateCategory;
  label: string;
}> = [
  { id: ERichMessageTemplateCategory.Square, label: strings.categorySquare },
  {
    id: ERichMessageTemplateCategory.Landscape,
    label: strings.categoryLandscape,
  },
  {
    id: ERichMessageTemplateCategory.Portrait,
    label: strings.categoryPortrait,
  },
  { id: ERichMessageTemplateCategory.Custom, label: strings.categoryCustom },
];

function groupBySize(templates: TRichMessageTemplate[]) {
  const groups = new Map<string, TRichMessageTemplate[]>();
  for (const template of templates) {
    const key = `${template.size.width} × ${template.size.height}`;
    const list = groups.get(key) ?? [];
    list.push(template);
    groups.set(key, list);
  }
  return Array.from(groups.entries());
}

export function RichMessageTemplateModal({
  open,
  selectedId,
  templates,
  onClose,
  onApply,
}: Props) {
  const [pendingId, setPendingId] = useState(selectedId);
  const [activeCategory, setActiveCategory] = useState(
    ERichMessageTemplateCategory.Square,
  );

  useEffect(() => {
    if (open) {
      setPendingId(selectedId);
      const selected = findRichMessageTemplate(templates, selectedId);
      if (selected) setActiveCategory(selected.category);
    }
  }, [open, selectedId, templates]);

  const categoryTemplates = useMemo(
    () =>
      templates.filter((t) => t.category === activeCategory).sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [activeCategory, templates],
  );

  const sizeGroups = useMemo(
    () => groupBySize(categoryTemplates),
    [categoryTemplates],
  );

  const handleApply = useCallback(() => {
    onApply(pendingId);
  }, [onApply, pendingId]);

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <div className="flex max-h-[min(85vh,800px)] flex-col">
        <div className="flex shrink-0 items-start justify-between border-b border-neutral-200 pb-4">
          <h2 className="text-base font-bold text-neutral-900">
            {strings.templateModalTitle}
          </h2>
          <button
            type="button"
            className="rounded-lg p-1 text-neutral-500 hover:bg-neutral-100"
            aria-label={commonStrings.close}
            onClick={onClose}
          >
            <Icon icon="close" width={20} height={20} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 gap-4 overflow-hidden py-4">
          <nav className="flex w-36 shrink-0 flex-col gap-1 border-r border-neutral-200 pr-3">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveCategory(tab.id)}
                className={`rounded-lg px-3 py-2 text-left text-sm font-medium ${
                  activeCategory === tab.id
                    ? 'bg-neutral-200 text-neutral-900'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {activeCategory === ERichMessageTemplateCategory.Custom ? (
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">
                  {strings.customSizeHint}
                </h3>
                <div className="mt-4 flex flex-wrap gap-4">
                  {categoryTemplates.map((template) => (
                    <RichMessageTemplateThumbnail
                      key={template.id}
                      template={template}
                      isSelected={pendingId === template.id}
                      onClick={() => setPendingId(template.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              sizeGroups.map(([sizeLabel, templates]) => (
                <section key={sizeLabel} className="mb-8 pl-2">
                  <h3 className="text-sm font-semibold text-neutral-900">
                    {sizeLabel} pixels
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {templates.map((template) => (
                      <RichMessageTemplateThumbnail
                        key={template.id}
                        template={template}
                        isSelected={pendingId === template.id}
                        onClick={() => setPendingId(template.id)}
                      />
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-neutral-200 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {strings.templateModalCancel}
          </Button>
          <Button type="button" onClick={handleApply}>
            {strings.templateModalApply}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
